import type { TrackingSport } from "@/lib/tracking/types";

export type SnowActivity = "ski" | "snowboard" | "mixed_snow";
export type RecordingActivity = SnowActivity | Exclude<TrackingSport, "ski">;
export type ActivityChoice = { version: "activity-choice-v1"; activity: RecordingActivity };

/** A choice made when recording starts. This is not sensor or timing verification. */
export function activityChoice(sport: TrackingSport, snow: SnowActivity | null): ActivityChoice | undefined {
  const activity = sport === "ski" ? snow : sport;
  return activity ? { version: "activity-choice-v1", activity } : undefined;
}

export function validActivityChoice(value: unknown, sport: TrackingSport): value is ActivityChoice {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const choice = value as Record<string, unknown>;
  return Object.keys(choice).length === 2 && choice.version === "activity-choice-v1"
    && typeof choice.activity === "string"
    && (sport === "ski" ? ["ski", "snowboard", "mixed_snow"].includes(choice.activity) : choice.activity === sport);
}

export function activityFromStats(stats: unknown, sport: TrackingSport): RecordingActivity | "snow_unspecified" {
  const choice = stats && typeof stats === "object" && "activityChoice" in stats ? stats.activityChoice : undefined;
  return validActivityChoice(choice, sport) ? choice.activity : sport === "ski" ? "snow_unspecified" : sport;
}

export function recordingActivityLabel(activity: RecordingActivity | "snow_unspecified"): string {
  return { ski: "Skiing", snowboard: "Snowboarding", mixed_snow: "Mixed snow sports", snow_unspecified: "Snow sports, category not recorded", mtb: "Mountain biking", coastal: "Water sports", surf: "Surfing" }[activity];
}

/** Recovery may preserve a choice, but cannot assign one to a legacy recording. */
export function preserveActivityChoice(
  saved: ActivityChoice | undefined,
  incoming: ActivityChoice | undefined,
  sport: TrackingSport,
): ActivityChoice | undefined {
  if (saved !== undefined && !validActivityChoice(saved, sport)
    || incoming !== undefined && !validActivityChoice(incoming, sport)
    || incoming !== undefined && saved?.activity !== incoming.activity) {
    throw new Error("The activity chosen at the start of this recording cannot be changed.");
  }
  return saved;
}
